import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import PortalAdminLayout from "./PortalAdminLayout";
import "./PortalDashboard.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

const toDateInputValue = (date) => {
  const value = new Date(date || Date.now());
  if (Number.isNaN(value.getTime())) return "";
  return value.toISOString().slice(0, 10);
};

const daysAgoDateInput = (days) => {
  const value = new Date();
  value.setDate(value.getDate() - Number(days || 0));
  return toDateInputValue(value);
};

const ProductTaxAudit = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [limit, setLimit] = useState(500);
  const [fromDate, setFromDate] = useState(() => daysAgoDateInput(30));
  const [toDate, setToDate] = useState(() => toDateInputValue(new Date()));
  const [audit, setAudit] = useState(null);
  const [pipelineSyncRunning, setPipelineSyncRunning] = useState(false);
  const [pipelineSyncMessage, setPipelineSyncMessage] = useState("");
  const [pipelineSyncError, setPipelineSyncError] = useState("");

  const checkPortalAdmin = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return false;
    }

    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => `%${("00" + c.charCodeAt(0).toString(16)).slice(-2)}`)
          .join(""),
      );

      const decoded = JSON.parse(jsonPayload);
      const hasPortalAdminAccess =
        decoded.role === "portal_admin" ||
        decoded.role === "R6" ||
        decoded.role === "R2" ||
        (decoded.roles &&
          (decoded.roles.includes("portal_admin") ||
            decoded.roles.includes("R6") ||
            decoded.roles.includes("R2")));

      if (!hasPortalAdminAccess) {
        navigate("/portal-admin");
        return false;
      }
      return true;
    } catch (_error) {
      navigate("/login");
      return false;
    }
  }, [navigate]);

  const fetchAudit = useCallback(async () => {
    const token = localStorage.getItem("ogestyle_token");
    setLoading(true);
    setError("");

    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/payout-ledger/admin/product-tax-audit?limit=${limit}&fromDate=${encodeURIComponent(
          fromDate || "",
        )}&toDate=${encodeURIComponent(toDate || "")}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setAudit(response.data);
    } catch (requestError) {
      const status = requestError?.response?.status;
      if (status === 403) {
        setError("Admin access required for product tax audit.");
      } else {
        setError("Failed to load product tax audit.");
      }
    } finally {
      setLoading(false);
    }
  }, [limit, fromDate, toDate]);

  const runPipelineSync = useCallback(async () => {
    const token = localStorage.getItem("ogestyle_token");
    setPipelineSyncRunning(true);
    setPipelineSyncMessage("");
    setPipelineSyncError("");

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/payout-ledger/admin/product-tax-audit/pipeline-sync`,
        { dryRun: false },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const data = response.data || {};
      setPipelineSyncMessage(
        `Pipeline sync complete: ${Number(data.upserted || 0)} inserted, ${Number(
          data.modified || 0,
        )} updated (${Number(data.writesPrepared || 0)} processed).`,
      );
      await fetchAudit();
    } catch (requestError) {
      const message =
        requestError?.response?.data?.message ||
        "Pipeline sync failed. Verify pipeline CSV environment paths are configured.";
      setPipelineSyncError(message);
    } finally {
      setPipelineSyncRunning(false);
    }
  }, [fetchAudit]);

  useEffect(() => {
    if (checkPortalAdmin()) {
      fetchAudit();
    }
  }, [checkPortalAdmin, fetchAudit]);

  const collectedSummary = audit?.collectedSummary || {
    totalTaxCollected: 0,
    totalTaxableCollected: 0,
    totalRecords: 0,
    bySourceType: {},
    byJurisdiction: {},
  };

  const pipelineSyncStatus = audit?.pipelineSyncStatus || null;
  const pipelineDatasetStatus = audit?.pipelineDatasetStatus || null;

  const collectedRows = useMemo(() => audit?.collectedRecords || [], [audit]);

  const complianceAudit = useMemo(() => {
    const missingTaxRate = [];
    const missingTaxBreakdown = [];
    const missingJurisdiction = [];
    const issueKeys = new Set();

    const toIssueRow = (row, details) => ({
      sourceType: row.sourceType,
      sourceId: String(row.sourceId || ""),
      createdAt: row.createdAt || null,
      taxAmount: Number(row.taxAmount || 0),
      taxRate: Number(row.taxRate || 0),
      taxableAmount: row.taxableAmount == null ? null : Number(row.taxableAmount || 0),
      details,
    });

    for (const row of collectedRows) {
      const taxAmount = Number(row.taxAmount || 0);
      const components = Array.isArray(row.taxBreakdown) ? row.taxBreakdown : [];
      const contributingComponents = components.filter(
        (part) =>
          Number(part?.amount || 0) > 0 ||
          Number(part?.taxableAmount || 0) > 0 ||
          Number(part?.rate || 0) > 0,
      );
      const effectiveRate = contributingComponents.reduce(
        (sum, part) => sum + Number(part?.rate || 0),
        0,
      );
      const hasComponentBreakdown = contributingComponents.length > 0;
      const hasSpecificJurisdiction = contributingComponents.some((part) => {
        const jurisdiction = String(part?.jurisdiction || "").trim().toUpperCase();
        return jurisdiction && jurisdiction !== "US";
      });

      let hasIssue = false;
      const issueKey = `${row.sourceType}-${row.sourceId}`;

      if (taxAmount > 0 && !hasComponentBreakdown) {
        missingTaxBreakdown.push(
          toIssueRow(row, "Tax amount is present but tax breakdown components are missing."),
        );
        hasIssue = true;
      }

      if (taxAmount > 0 && Number(row.taxRate || 0) <= 0 && effectiveRate <= 0) {
        missingTaxRate.push(
          toIssueRow(row, "Tax amount is present but no tax rate is available from Stripe components."),
        );
        hasIssue = true;
      }

      if (taxAmount > 0 && hasComponentBreakdown && !hasSpecificJurisdiction) {
        missingJurisdiction.push(
          toIssueRow(row, "Tax components do not include specific jurisdiction (only generic/blank)."),
        );
        hasIssue = true;
      }

      if (hasIssue) {
        issueKeys.add(issueKey);
      }
    }

    const totalEligible = collectedRows.length;
    const issueCount = issueKeys.size;
    const compliant = Math.max(0, totalEligible - issueCount);

    return {
      totalEligible,
      compliant,
      issueCount,
      missingTaxRate,
      missingTaxBreakdown,
      missingJurisdiction,
    };
  }, [collectedRows]);

  const compliancePct = useMemo(() => {
    if (!complianceAudit.totalEligible) return 0;
    return (complianceAudit.compliant / complianceAudit.totalEligible) * 100;
  }, [complianceAudit.compliant, complianceAudit.totalEligible]);

  const collectedJurisdictionRows = useMemo(() => {
    const entries = Object.entries(collectedSummary.byJurisdiction || {});
    return entries
      .map(([jurisdiction, data]) => ({
        jurisdiction,
        count: Number(data?.count || 0),
        taxCollected: Number(data?.taxCollected || 0),
        taxableAmount: Number(data?.taxableAmount || 0),
      }))
      .sort((a, b) => b.taxCollected - a.taxCollected);
  }, [collectedSummary.byJurisdiction]);

  const csvEscape = (value) => {
    const text = value == null ? "" : String(value);
    if (/[",\n]/.test(text)) {
      return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
  };

  const downloadCsv = (rows, filename) => {
    if (!rows || rows.length === 0) return;
    const headers = Object.keys(rows[0]);
    const lines = [
      headers.join(","),
      ...rows.map((row) => headers.map((h) => csvEscape(row[h])).join(",")),
    ];
    const csv = lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  const exportCollectedBookingLevelCsv = () => {
    const rows = collectedRows.map((row) => {
      const components = Array.isArray(row.taxBreakdown) ? row.taxBreakdown : [];
      const jurisdictions = [...new Set(components.map((part) => part?.jurisdiction).filter(Boolean))].join(" | ");
      const componentSummary = components
        .map((part) => {
          const label = part?.name || part?.jurisdiction || "Sales Tax";
          const level = part?.level ? `:${part.level}` : "";
          const rate = part?.rate != null ? `@${Number(part.rate).toFixed(2)}%` : "";
          return `${label}${level}${rate}`;
        })
        .join("; ");

      return {
        sourceType: row.sourceType,
        sourceId: String(row.sourceId || ""),
        createdAt: row.createdAt ? new Date(row.createdAt).toISOString() : "",
        paymentStatus: row.paymentStatus || "",
        status: row.status || "",
        currency: row.currency || "USD",
        subtotal: Number(row.subtotal || 0).toFixed(2),
        taxAmount: Number(row.taxAmount || 0).toFixed(2),
        taxRate: row.taxRate == null ? "" : Number(row.taxRate).toFixed(4),
        taxableAmount:
          row.taxableAmount == null ? "" : Number(row.taxableAmount).toFixed(2),
        state: row.state || "",
        city: row.city || "",
        district: row.district || "",
        county: row.county || "",
        componentCount: Number(row.componentCount || 0),
        jurisdictions,
        componentSummary,
      };
    });

    downloadCsv(rows, "product-tax-collections-booking-level.csv");
  };

  const exportCollectedComponentLevelCsv = () => {
    const rows = [];
    for (const row of collectedRows) {
      const components = Array.isArray(row.taxBreakdown) ? row.taxBreakdown : [];
      if (components.length === 0) {
        rows.push({
          sourceType: row.sourceType,
          sourceId: String(row.sourceId || ""),
          createdAt: row.createdAt
            ? new Date(row.createdAt).toISOString()
            : "",
          componentName: "",
          componentLevel: "",
          componentJurisdiction: "",
          componentRate: "",
          componentTaxableAmount: "",
          componentTaxAmount: "",
          subtotal: Number(row.subtotal || 0).toFixed(2),
          taxAmount: Number(row.taxAmount || 0).toFixed(2),
          taxRate: row.taxRate == null ? "" : Number(row.taxRate).toFixed(4),
          taxableAmount: Number(row.taxableAmount || 0).toFixed(2),
        });
        continue;
      }

      for (const part of components) {
        rows.push({
          sourceType: row.sourceType,
          sourceId: String(row.sourceId || ""),
          createdAt: row.createdAt
            ? new Date(row.createdAt).toISOString()
            : "",
          componentName: part?.name || "",
          componentLevel: part?.level || "",
          componentJurisdiction: part?.jurisdiction || "",
          componentRate:
            part?.rate == null ? "" : Number(part.rate).toFixed(4),
          componentTaxableAmount:
            part?.taxableAmount == null
              ? ""
              : Number(part.taxableAmount).toFixed(2),
          componentTaxAmount:
            part?.amount == null ? "" : Number(part.amount).toFixed(2),
          subtotal: Number(row.subtotal || 0).toFixed(2),
          taxAmount: Number(row.taxAmount || 0).toFixed(2),
          taxRate: row.taxRate == null ? "" : Number(row.taxRate).toFixed(4),
          taxableAmount:
            row.taxableAmount == null ? "" : Number(row.taxableAmount).toFixed(2),
          state: row.state || "",
          city: row.city || "",
          district: row.district || "",
          county: row.county || "",
        });
      }
    }

    downloadCsv(rows, "product-tax-collections-component-level.csv");
  };

  // Deduplicated list of all exception findings for the compliance section
  const allFindings = useMemo(() => {
    const r = audit?.records || {};
    const combined = [
      ...(r.missingTaxRate || []),
      ...(r.zeroTaxWithProducts || []),
      ...(r.noLedgerEntry || []),
    ];
    const seen = new Set();
    return combined.filter((row) => {
      const key = String(row.bookingId);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [audit]);

  const exportBookingLevelCsv = () => {
    const rows = allFindings.map((row) => ({
      bookingId: String(row.bookingId || ""),
      createdAt: row.createdAt ? new Date(row.createdAt).toISOString() : "",
      productsTotalPrice: Number(row.productsTotalPrice || 0).toFixed(2),
      tax: Number(row.tax || 0).toFixed(2),
      taxRate: row.taxRate == null ? "" : Number(row.taxRate).toFixed(4),
      taxableAmount: (
        Array.isArray(row.taxBreakdown)
          ? row.taxBreakdown.reduce((s, p) => s + Number(p?.taxableAmount || 0), 0)
          : 0
      ).toFixed(2),
      hasLedgerEntry: row.hasLedgerEntry ? "yes" : "no",
    }));
    downloadCsv(rows, "product-tax-audit-exceptions-booking.csv");
  };

  const exportComponentLevelCsv = () => {
    const rows = [];
    for (const row of allFindings) {
      const components = Array.isArray(row.taxBreakdown) ? row.taxBreakdown : [];
      if (components.length === 0) {
        rows.push({
          bookingId: String(row.bookingId || ""),
          createdAt: row.createdAt ? new Date(row.createdAt).toISOString() : "",
          componentName: "", componentLevel: "", componentJurisdiction: "",
          componentRate: "", componentTaxableAmount: "", componentTaxAmount: "",
          tax: Number(row.tax || 0).toFixed(2),
        });
        continue;
      }
      for (const part of components) {
        rows.push({
          bookingId: String(row.bookingId || ""),
          createdAt: row.createdAt ? new Date(row.createdAt).toISOString() : "",
          componentName: part?.name || "",
          componentLevel: part?.level || "",
          componentJurisdiction: part?.jurisdiction || "",
          componentRate: part?.rate == null ? "" : Number(part.rate).toFixed(4),
          componentTaxableAmount: part?.taxableAmount == null ? "" : Number(part.taxableAmount).toFixed(2),
          componentTaxAmount: part?.amount == null ? "" : Number(part.amount).toFixed(2),
          tax: Number(row.tax || 0).toFixed(2),
        });
      }
    }
    downloadCsv(rows, "product-tax-audit-exceptions-component.csv");
  };

  const StatCard = ({ title, value, color = "#3b82f6", subtitle = "" }) => (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        padding: "16px",
      }}
    >
      <div style={{ fontSize: "13px", color: "#6b7280", marginBottom: "6px" }}>
        {title}
      </div>
      <div style={{ fontSize: "26px", fontWeight: 700, color }}>{value}</div>
      {subtitle ? (
        <div style={{ marginTop: "4px", color: "#6b7280", fontSize: "12px" }}>
          {subtitle}
        </div>
      ) : null}
    </div>
  );

  const SectionTable = ({ title, rows }) => (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        padding: "16px",
        marginBottom: "16px",
      }}
    >
      <h3 style={{ marginTop: 0, marginBottom: "12px", color: "#111827" }}>
        {title} ({rows.length})
      </h3>
      {rows.length === 0 ? (
        <p style={{ color: "#6b7280", margin: 0 }}>No records found.</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Transaction</th>
                <th style={thStyle}>Created</th>
                <th style={thStyle}>Tax Amount</th>
                <th style={thStyle}>Tax</th>
                <th style={thStyle}>Tax Rate</th>
                <th style={thStyle}>Taxable Base</th>
                <th style={thStyle}>Details</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 100).map((row) => (
                <tr key={`${title}-${row.sourceType}-${row.sourceId}`}>
                  <td style={tdStyle}>{row.sourceType || "-"}</td>
                  <td style={tdStyle}>{String(row.sourceId || "-")}</td>
                  <td style={tdStyle}>
                    {row.createdAt
                      ? new Date(row.createdAt).toLocaleString()
                      : "-"}
                  </td>
                  <td style={tdStyle}>${Number(row.taxAmount || 0).toFixed(2)}</td>
                  <td style={tdStyle}>${Number(row.taxAmount || 0).toFixed(2)}</td>
                  <td style={tdStyle}>
                    {Number(row.taxRate || 0) > 0
                      ? `${Number(row.taxRate).toFixed(2)}%`
                      : "-"}
                  </td>
                  <td style={tdStyle}>
                    {row.taxableAmount == null
                      ? "-"
                      : `$${Number(row.taxableAmount || 0).toFixed(2)}`}
                  </td>
                  <td style={tdStyle}>{row.details || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length > 100 ? (
            <p style={{ marginTop: "10px", color: "#6b7280", fontSize: "12px" }}>
              Showing first 100 rows.
            </p>
          ) : null}
        </div>
      )}
    </div>
  );

  const fmtUsd = (value) =>
    `$${Number(value || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const fmtUsdOrDash = (value) =>
    value == null ? "-" : fmtUsd(value);

  return (
    <PortalAdminLayout>
      <div className="portal-dashboard">
        <div className="dashboard-header">
          <h1>Product Tax Collections</h1>
          <p>Tax collected on product-bearing paid bookings within the selected date range.</p>
        </div>

        {/* ── Filter bar ── */}
        <div
          style={{
            background: "white",
            border: "1px solid #e5e7eb",
            borderRadius: "12px",
            padding: "16px",
            marginBottom: "16px",
            display: "flex",
            gap: "12px",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <label style={{ fontWeight: 600, whiteSpace: "nowrap" }}>From</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={{ padding: "8px", borderRadius: "8px", border: "1px solid #d1d5db" }}
          />
          <label style={{ fontWeight: 600, whiteSpace: "nowrap" }}>To</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={{ padding: "8px", borderRadius: "8px", border: "1px solid #d1d5db" }}
          />
          <label htmlFor="scan-limit" style={{ fontWeight: 600, whiteSpace: "nowrap" }}>
            Scan limit
          </label>
          <input
            id="scan-limit"
            type="number"
            min="50"
            max="5000"
            step="50"
            value={limit}
            onChange={(e) =>
              setLimit(Math.max(50, Math.min(5000, Number(e.target.value) || 500)))
            }
            style={{ width: "100px", padding: "8px", borderRadius: "8px", border: "1px solid #d1d5db" }}
          />
          <button
            type="button"
            onClick={fetchAudit}
            disabled={loading}
            style={{
              background: "#2563eb",
              color: "white",
              border: "none",
              borderRadius: "8px",
              padding: "9px 18px",
              cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.7 : 1,
              fontWeight: 600,
            }}
          >
            {loading ? "Loading..." : "Run"}
          </button>
          <button
            type="button"
            onClick={runPipelineSync}
            disabled={pipelineSyncRunning}
            style={{
              background: "#0f766e",
              color: "white",
              border: "none",
              borderRadius: "8px",
              padding: "9px 18px",
              cursor: pipelineSyncRunning ? "not-allowed" : "pointer",
              opacity: pipelineSyncRunning ? 0.7 : 1,
              fontWeight: 600,
            }}
          >
            {pipelineSyncRunning ? "Syncing..." : "Run Pipeline Sync"}
          </button>
        </div>

        {pipelineSyncMessage ? (
          <div
            style={{
              background: "#ecfdf5",
              color: "#065f46",
              border: "1px solid #a7f3d0",
              borderRadius: "10px",
              padding: "12px 14px",
              marginBottom: "16px",
            }}
          >
            {pipelineSyncMessage}
          </div>
        ) : null}

        <div
          style={{
            background: "white",
            border: "1px solid #e5e7eb",
            borderRadius: "12px",
            padding: "12px 14px",
            marginBottom: "16px",
          }}
        >
          <div style={{ fontWeight: 700, color: "#111827", marginBottom: "6px" }}>
            Last Pipeline Sync
          </div>
          {pipelineSyncStatus ? (
            <div style={{ color: "#374151", fontSize: "13px", lineHeight: 1.5 }}>
              <div>
                Status: {pipelineSyncStatus.success ? "Success" : "Failed"}
                {pipelineSyncStatus.trigger ? ` (${pipelineSyncStatus.trigger})` : ""}
              </div>
              <div>
                Finished: {pipelineSyncStatus.finishedAt
                  ? new Date(pipelineSyncStatus.finishedAt).toLocaleString()
                  : "-"}
              </div>
              <div>
                Processed: {Number(pipelineSyncStatus.writesPrepared || 0)} | Inserted: {Number(pipelineSyncStatus.upserted || 0)} | Updated: {Number(pipelineSyncStatus.modified || 0)}
              </div>
              {pipelineSyncStatus.message ? (
                <div style={{ color: pipelineSyncStatus.success ? "#065f46" : "#991b1b" }}>
                  {pipelineSyncStatus.message}
                </div>
              ) : null}
            </div>
          ) : (
            <div style={{ color: "#6b7280", fontSize: "13px" }}>
              No pipeline sync has been logged yet.
            </div>
          )}
        </div>

        <div
          style={{
            background: "white",
            border: "1px solid #e5e7eb",
            borderRadius: "12px",
            padding: "12px 14px",
            marginBottom: "16px",
          }}
        >
          <div style={{ fontWeight: 700, color: "#111827", marginBottom: "6px" }}>
            Pipeline Dataset Scan
          </div>
          {pipelineDatasetStatus ? (
            <div style={{ color: "#374151", fontSize: "13px", lineHeight: 1.5 }}>
              <div>
                Source: {pipelineDatasetStatus.sourcePath || "-"}
              </div>
              <div>
                Prefix: {pipelineDatasetStatus.scanPrefix || "(container root)"}
              </div>
              {pipelineDatasetStatus.available ? (
                <>
                  <div>
                    Blobs scanned: {Number(pipelineDatasetStatus.scannedBlobs || 0)}
                    {pipelineDatasetStatus.truncated ? " (capped)" : ""}
                  </div>
                  <div>
                    Default sync target: {pipelineDatasetStatus.defaultSyncTargetAvailable
                      ? "Ready"
                      : "Waiting for tax tables"}
                  </div>
                  {pipelineDatasetStatus.defaultSyncTargetPath ? (
                    <div style={{ color: "#065f46" }}>
                      {pipelineDatasetStatus.defaultSyncTargetPath}
                    </div>
                  ) : null}
                  <div>
                    Latest prefix tax tables: tax_transactions {pipelineDatasetStatus.requiredDatasetsInLatestPrefix?.taxTransactions ? "yes" : "no"} | tax_transaction_jurisdiction_details {pipelineDatasetStatus.requiredDatasetsInLatestPrefix?.jurisdictionDetails ? "yes" : "no"}
                  </div>
                  {Array.isArray(pipelineDatasetStatus.latestPrefixes) &&
                  pipelineDatasetStatus.latestPrefixes.length > 0 ? (
                    <div style={{ marginTop: "6px" }}>
                      {pipelineDatasetStatus.latestPrefixes.slice(0, 5).map((prefixRow) => (
                        <div key={prefixRow.prefix}>
                          {prefixRow.prefix} • {prefixRow.latestModified ? new Date(prefixRow.latestModified).toLocaleString() : "-"} • tax_transactions {prefixRow.hasTaxTransactions ? "yes" : "no"} • jurisdiction_details {prefixRow.hasJurisdictionDetails ? "yes" : "no"}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ marginTop: "6px", color: "#6b7280" }}>
                      No pipeline prefixes detected.
                    </div>
                  )}
                </>
              ) : (
                <div style={{ color: "#b45309" }}>
                  {pipelineDatasetStatus.reason || "Pipeline dataset scan is unavailable."}
                </div>
              )}
            </div>
          ) : (
            <div style={{ color: "#6b7280", fontSize: "13px" }}>
              Pipeline dataset scan is not available yet.
            </div>
          )}
        </div>

        {pipelineSyncError ? (
          <div
            style={{
              background: "#fef2f2",
              color: "#991b1b",
              border: "1px solid #fecaca",
              borderRadius: "10px",
              padding: "12px 14px",
              marginBottom: "16px",
            }}
          >
            {pipelineSyncError}
          </div>
        ) : null}

        {error ? (
          <div
            style={{
              background: "#fef2f2",
              color: "#991b1b",
              border: "1px solid #fecaca",
              borderRadius: "10px",
              padding: "12px 14px",
              marginBottom: "16px",
            }}
          >
            {error}
          </div>
        ) : null}

        {/* ── Tax Collected Summary ── */}
        <div
          style={{
            display: "grid",
            gap: "12px",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            marginBottom: "16px",
          }}
        >
          <StatCard
            title="Tax Collected"
            value={fmtUsd(collectedSummary.totalTaxCollected)}
            color="#059669"
            subtitle="Total tax amount in range"
          />
          <StatCard
            title="Taxable Amount"
            value={fmtUsd(collectedSummary.totalTaxableCollected)}
            subtitle="Sum of taxable bases"
          />
          <StatCard
            title="Transactions"
            value={collectedSummary.totalRecords}
            subtitle="With tax recorded"
          />
          <StatCard
            title="Jurisdictions"
            value={collectedJurisdictionRows.length}
            subtitle="Tax jurisdictions found"
          />
        </div>

        {/* ── Jurisdiction Breakdown ── */}
        {collectedJurisdictionRows.length > 0 && (
          <div
            style={{
              background: "white",
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
              padding: "16px",
              marginBottom: "16px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "12px",
              }}
            >
              <h3 style={{ margin: 0, color: "#111827" }}>
                Tax by Jurisdiction ({collectedJurisdictionRows.length})
              </h3>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Jurisdiction</th>
                    <th style={thStyle}>Transactions</th>
                    <th style={thStyle}>Taxable Amount</th>
                    <th style={thStyle}>Tax Collected</th>
                  </tr>
                </thead>
                <tbody>
                  {collectedJurisdictionRows.map((row) => (
                    <tr key={row.jurisdiction}>
                      <td style={tdStyle}>{row.jurisdiction}</td>
                      <td style={tdStyle}>{row.count}</td>
                      <td style={tdStyle}>{fmtUsd(row.taxableAmount)}</td>
                      <td style={{ ...tdStyle, fontWeight: 600, color: "#059669" }}>
                        {fmtUsd(row.taxCollected)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Collected Tax Records Table + Export ── */}
        <div
          style={{
            background: "white",
            border: "1px solid #e5e7eb",
            borderRadius: "12px",
            padding: "16px",
            marginBottom: "16px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "8px",
              marginBottom: "12px",
            }}
          >
            <h3 style={{ margin: 0, color: "#111827" }}>
              Collected Tax Records ({collectedRows.length})
            </h3>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={exportCollectedBookingLevelCsv}
                disabled={loading || collectedRows.length === 0}
                style={{
                  background: "#0f766e",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  padding: "8px 14px",
                  fontSize: "13px",
                  cursor: loading || collectedRows.length === 0 ? "not-allowed" : "pointer",
                  opacity: loading || collectedRows.length === 0 ? 0.6 : 1,
                }}
              >
                Export Summary CSV
              </button>
              <button
                type="button"
                onClick={exportCollectedComponentLevelCsv}
                disabled={loading || collectedRows.length === 0}
                style={{
                  background: "#0b4a6f",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  padding: "8px 14px",
                  fontSize: "13px",
                  cursor: loading || collectedRows.length === 0 ? "not-allowed" : "pointer",
                  opacity: loading || collectedRows.length === 0 ? 0.6 : 1,
                }}
              >
                Export Component CSV
              </button>
            </div>
          </div>

          {collectedRows.length === 0 ? (
            <p style={{ color: "#6b7280", margin: 0 }}>
              {loading
                ? "Loading..."
                : "No tax records found in this date range. Transactions may not have tax recorded yet — run a backfill to pull tax data from Stripe."}
            </p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Type</th>
                    <th style={thStyle}>ID</th>
                    <th style={thStyle}>Date</th>
                    <th style={thStyle}>Subtotal</th>
                    <th style={thStyle}>Taxable</th>
                    <th style={thStyle}>Tax Amount</th>
                    <th style={thStyle}>Rate</th>
                    <th style={thStyle}>State</th>
                    <th style={thStyle}>City</th>
                    <th style={thStyle}>District</th>
                    <th style={thStyle}>County</th>
                    <th style={thStyle}>Components</th>
                  </tr>
                </thead>
                <tbody>
                  {collectedRows.slice(0, 200).map((row) => (
                    <tr key={`${row.sourceType}-${row.sourceId}`}>
                      <td style={tdStyle}>{row.sourceType}</td>
                      <td style={{ ...tdStyle, fontSize: "11px", color: "#6b7280" }}>
                        {String(row.sourceId).slice(-8)}
                      </td>
                      <td style={tdStyle}>
                        {row.createdAt ? new Date(row.createdAt).toLocaleDateString() : "-"}
                      </td>
                      <td style={tdStyle}>{fmtUsd(row.subtotal)}</td>
                      <td style={tdStyle}>{fmtUsdOrDash(row.taxableAmount)}</td>
                      <td style={{ ...tdStyle, fontWeight: 600, color: "#059669" }}>
                        {fmtUsd(row.taxAmount)}
                      </td>
                      <td style={tdStyle}>
                        {row.taxRate ? `${Number(row.taxRate).toFixed(2)}%` : "-"}
                      </td>
                      <td style={tdStyle}>{row.state || "-"}</td>
                      <td style={tdStyle}>{row.city || "-"}</td>
                      <td style={tdStyle}>{row.district || "-"}</td>
                      <td style={tdStyle}>{row.county || "-"}</td>
                      <td style={{ ...tdStyle, fontSize: "11px", color: "#374151", maxWidth: "260px" }}>
                        {Array.isArray(row.taxBreakdown) && row.taxBreakdown.length > 0
                          ? row.taxBreakdown
                              .map((part) => {
                                const label = part?.name || part?.jurisdiction || "Sales Tax";
                                const rate = part?.rate != null ? ` ${Number(part.rate).toFixed(2)}%` : "";
                                const amt = part?.amount != null ? ` (${fmtUsd(part.amount)})` : "";
                                return `${label}${rate}${amt}`;
                              })
                              .join(" · ")
                          : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {collectedRows.length > 200 && (
                <p style={{ marginTop: "8px", color: "#6b7280", fontSize: "12px" }}>
                  Showing first 200 rows. Use Export CSV for full dataset.
                </p>
              )}
            </div>
          )}
        </div>

        {/* ── Compliance / Exception Audit (secondary) ── */}
        <details style={{ marginBottom: "16px" }}>
          <summary
            style={{
              cursor: "pointer",
              fontWeight: 600,
              fontSize: "15px",
              color: "#374151",
              padding: "10px 0",
              listStyle: "none",
            }}
          >
            ▶ Tax Compliance Audit (All Tax-Eligible Transactions) — {complianceAudit.totalEligible} scanned, {complianceAudit.compliant} compliant ({compliancePct.toFixed(1)}%)
            {complianceAudit.issueCount > 0 && (
              <span style={{ marginLeft: "8px", color: "#dc2626" }}>
                ({complianceAudit.issueCount} issues)
              </span>
            )}
          </summary>
          <div style={{ paddingTop: "12px" }}>
            <p style={{ margin: "0 0 12px", color: "#6b7280", fontSize: "13px" }}>
              Collected tax records in selected date range (bookings + orders): {collectedSummary.totalRecords}
            </p>
            <div
              style={{
                display: "grid",
                gap: "12px",
                gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                marginBottom: "16px",
              }}
            >
              <StatCard
                title="Total Eligible"
                value={complianceAudit.totalEligible}
                subtitle="Bookings + orders with tax records"
              />
              <StatCard title="Scanned" value={complianceAudit.totalEligible} subtitle="Entire selected range" />
              <StatCard title="Compliant" value={complianceAudit.compliant} color="#059669" subtitle={`${compliancePct.toFixed(1)}%`} />
              <StatCard title="Missing Tax Rate" value={complianceAudit.missingTaxRate.length} color="#dc2626" />
              <StatCard title="Missing Breakdown" value={complianceAudit.missingTaxBreakdown.length} color="#ea580c" />
              <StatCard title="Missing Jurisdiction" value={complianceAudit.missingJurisdiction.length} color="#b91c1c" />
            </div>
            <SectionTable title="Missing Tax Rate" rows={complianceAudit.missingTaxRate || []} />
            <SectionTable title="Missing Tax Breakdown" rows={complianceAudit.missingTaxBreakdown || []} />
            <SectionTable title="Missing Specific Jurisdiction" rows={complianceAudit.missingJurisdiction || []} />
          </div>
        </details>
      </div>
    </PortalAdminLayout>
  );
};

const thStyle = {
  textAlign: "left",
  fontSize: "12px",
  color: "#6b7280",
  fontWeight: 600,
  padding: "10px 8px",
  borderBottom: "1px solid #e5e7eb",
};

const tdStyle = {
  padding: "10px 8px",
  borderBottom: "1px solid #f3f4f6",
  fontSize: "13px",
  color: "#111827",
};

export default ProductTaxAudit;
